import { Injectable, Inject, Optional } from '@angular/core';
import { SocialUser, GoogleLoginProvider, FacebookLoginProvider, AuthService } from 'angularx-social-login';

import {
  SessionService,
  UserService,
  ProfileService,
  RecoveryService,
} from '@relevance/angular-core';
import { ISession, IUser, IProfile, IGenericProfile } from '@relevance/core';

import {
  AccountServiceConfig,
  ACCOUNT_CONFIG,
  AnonymousUserFactory,
  ANONYMOUS_USER,
  SocialUserProfileFactory,
  SOCIAL_USER_PROFILE,
} from './account.config';

/**
 * This service handle account management.
 */
@Injectable()
export class AccountService<S extends ISession = ISession, U extends IUser = IUser, P extends IProfile = IGenericProfile> {

  private currentSession: S = null;
  private currentUser: U = {} as U;

  constructor(
    private oauthService: AuthService,
    private sessionService: SessionService<S>,
    private userService: UserService<U>,
    private profileService: ProfileService<P>,
    private recoveryService: RecoveryService,
    @Inject(ACCOUNT_CONFIG) private config: AccountServiceConfig,
    @Optional() @Inject(ANONYMOUS_USER) private anonymousUser: AnonymousUserFactory<U> = null,
    @Optional() @Inject(SOCIAL_USER_PROFILE) private socialUserProfile: SocialUserProfileFactory<P> = null,
  ) {
    this.reload();
  }

  /**
   * Get the storage backend.
   */
  private getStorage(): Storage {
    return window[this.config.storageEngine || 'sessionStorage'];
  }

  /**
   * Get the anonymous user object.
   */
  private createAnonymousUser(): U|null {
    if (this.anonymousUser) {
      return this.anonymousUser();
    }
    return null;
  }

  /**
   * Get the user profile from a social login user.
   */
  private getProfileFromSocialUser(socialUser: SocialUser): P {
    if (this.socialUserProfile) {
      return this.socialUserProfile(socialUser);
    }
    return {} as P;
  }

  /**
   * Reload the current state.
   */
  private reload(): Promise<void> {
    return new Promise((resolve) => {
      this.loadCurrentContext();

      if (this.currentSession) {
        this.getUser()
          .then((user) => {
            this.currentUser = user;
            this.saveCurrentContext();
            resolve();
          })
          .catch(() => this.clearCurrentContext());
      } else {
        this.clearCurrentContext();
        resolve();
      }
    });
  }

  /**
   * Save the current state.
   */
  private saveCurrentContext(): void {
    const prefix = this.config.storagePrefix || 'relevance';
    this.getStorage().setItem(`${prefix}-session`, JSON.stringify(this.currentSession));
    this.getStorage().setItem(`${prefix}-user`, JSON.stringify(this.currentUser));

    if (!this.isLoggedIn()) {
      this.getStorage().setItem(`${prefix}-anon`, JSON.stringify(this.currentUser));
    }
  }

  /**
   * Load the previous stage.
   */
  private loadCurrentContext(force: boolean = false): void {
    if (this.currentSession && this.currentUser && !force) {
      return;
    }

    const prefix = this.config.storagePrefix || 'relevance';
    const sessionData = this.getStorage().getItem(`${prefix}-session`);
    const userData = this.getStorage().getItem(`${prefix}-user`);
    this.currentSession = sessionData ? JSON.parse(sessionData) : null;
    this.currentUser = userData ? JSON.parse(userData) : this.getAnonymousUser();

    if (this.currentSession) {
      this.sessionService.setLastSession(this.currentSession);
    }
  }

  /**
   * Clear the previous state.
   */
  private clearCurrentContext(): void {
    this.currentSession = null;
    this.currentUser = this.createAnonymousUser();
    this.saveCurrentContext();
  }

  /**
   * Get the service configuration.
   */
  public getConfig(): AccountServiceConfig {
    return this.config;
  }

  /**
   * Check if a user ID is the currently logged in user.
   */
  public isCurrentUser(userId: string): boolean {
    if (userId === 'me') {
      return true;
    }
    if (!this.isLoggedIn()) {
      return false;
    }
    return this.currentUser.id === userId;
  }

  /**
   * Get the anonymous user.
   */
  public getAnonymousUser(): U {
    if (!this.isLoggedIn() && this.currentUser) {
      return this.getCurrentUser();
    }

    const prefix = this.config.storagePrefix || 'relevance';
    const anonData = this.getStorage().getItem(`${prefix}-anon`);
    return anonData ? JSON.parse(anonData) : this.createAnonymousUser();
  }

  /**
   * Clear the anonymous user.
   */
  public clearAnonymousUser(): void {
    const prefix = this.config.storagePrefix || 'relevance';
    this.getStorage().removeItem(`${prefix}-anon`);
  }

  /**
   * Get the currently active user.
   */
  public getCurrentUser(): U {
    return this.currentUser;
  }

  /**
   * Get the currently active session.
   */
  public getCurrentSession(): S {
    return this.currentSession;
  }

  /**
   * Check whether the current session is a valid one.
   */
  public isLoggedIn(): boolean {
    if (!this.currentSession || !this.currentUser) {
      return false;
    }
    return new Date(this.currentSession.expires_at) > new Date();
  }

  /**
   * Get the currently active session.
   */
  public getSession(sessionId: string = 'me'): Promise<S> {
    return new Promise((resolve) => {
      if (sessionId === 'me') {
        if (this.currentSession) {
          resolve(this.currentSession);
        } else {
          resolve(this.sessionService.getLastSession());
        }
      }
    });
  }

  /**
   * Get a user.
   */
  public getUser(userId: string = 'me'): Promise<U> {
    return new Promise((resolve, reject) => {
      this.userService.getUser(userId).then(resolve, reject);
    });
  }

  /**
   * Log in a user.
   */
  public loginUser(email: string, password: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.sessionService
        .createSession(email, password)
        .then(async (session) => {
          this.currentSession = session;
          this.currentUser = await this.getUser();
          this.saveCurrentContext();
          resolve();
        })
        .catch((err) => {
          console.error('user login failed', err);
          reject(err);
        });
    });
  }

  /**
   * Create a user account.
   */
  public createNewUser(
    email: string,
    password: string,
    data?: P,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.userService
        .createUser<P>(email, password, data)
        .then(async () => {
          await this.loginUser(email, password);
          resolve();
        })
        .catch((err) => {
          console.error('user registration failed', err);
          reject(err);
        });
    });
  }

  /**
   *  Handle login from third party. Returns True if the user has been created,
   *  false if it was an existing user.
   */
  public loginUserFromSocialProvider(
    providerName: 'facebook'|'google',
  ): Promise<boolean|null> {
    const providerId = {
      facebook: FacebookLoginProvider.PROVIDER_ID,
      google: GoogleLoginProvider.PROVIDER_ID,
    }[providerName];

    return new Promise((resolve, reject) => {
      this.oauthService
        .signIn(providerId)
        .then((socialUser) => {
          const login = socialUser.email;
          const secret = socialUser.id;
          const profile = this.getProfileFromSocialUser(socialUser);

          this.createNewUser(login, secret, profile)
            .then(() => resolve(true))
            .catch((response: any) => {
              if (response.error !== 'ObjectExistsError') {
                return reject(response);
              }

              this.loginUser(login, secret)
                .then((session) => resolve(false))
                .catch(reject);
            });
        })
        .catch((err) => {
          if (err.toString().indexOf('cancel') < 0) {
            console.error('social login error', err);
            reject(err);
          } else {
            resolve(null);
          }
        });
    });
  }

  /**
   * Log out.
   */
  public async logout(): Promise<void> {
    this.clearCurrentContext();
    this.sessionService.deleteSession()
      .then(() => this.sessionService.clearLastSession())
      .catch(() => null);
  }

  /**
   * Set user data.
   */
  public async setData(data: { [key: string]: any }): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isLoggedIn()) {
        this.profileService.updateProfile<any, U>(data)
          .then((user) => {
            this.currentUser = user;
            this.saveCurrentContext();
            resolve();
          })
          .catch((err) => {
            console.error('user update error', err);
            reject(err);
          });
      } else {
        if (this.currentUser) {
          for (const key of Object.keys(data)) {
            this.currentUser[key] = data[key];
          }
          this.saveCurrentContext();
        }
        resolve();
      }
    });
  }

  /**
   * Get user data.
   */
  public getData(name: string, defaultValue?: any): any {
    if (!this.currentUser) {
      return defaultValue;
    }
    return this.currentUser[name] || defaultValue;
  }

  /**
   * Update the account email address.
   */
  public requestEmailChange(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.userService.updateEmail(email)
        .then(() => resolve())
        .catch((err) => {
          console.error('user update email request error', err);
          reject(err);
        });
    });
  }

  /**
   * Send the email confirm email again.
   */
  public resendEmailChange(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.currentUser.email_pending) {
        return reject(new Error('there are no pending email confirmation'));
      }
      this.requestEmailChange(this.currentUser.email_pending).then(resolve, reject);
    });
  }

  /**
   * Confirm an email address.
   */
  public confirmEmail(token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.userService.confirmEmail(token)
        .then(() => resolve())
        .catch((err) => {
          console.error('user confirm email error', err);
          reject(err);
        });
    });
  }

  /**
   * Update the account email address.
   */
  public updatePassword(oldPassword: string, newPassword: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.userService.updateSecret(oldPassword, newPassword)
        .then(() => resolve())
        .catch((err) => {
          console.error('user password update error', err);
          reject(err);
        });
    });
  }

  /**
   * Recover a lost password.
   */
  public requestLostPassword(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.recoveryService.requestResetSecret(email)
        .then(() => resolve())
        .catch((err) => {
          console.error('user lost password request error', err);
          reject(err);
        });
    });
  }

  /**
   * Change a lost password.
   */
  public confirmLostPassword(token: string, password: string): Promise<U> {
    return new Promise((resolve, reject) => {
      this.recoveryService.confirmResetSecret<U>(token, password)
        .then((user) => resolve(user))
        .catch((err) => {
          console.error('user recover password error', err);
          reject(err);
        });
    });
  }

  /**
   * Validate a user token.
   */
  public validateUserToken(type: string, token: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.recoveryService.validateUserToken(type, token)
        .then(resolve)
        .catch(reject);
    });
  }

}
